// @core/stores/task/TaskTaskTypeStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { taskApi } from '@core/api';
import type {
  RegisterTaskTypeRequest,
  TaskTypeDefinition,
  TaskTypeDefinitionResponse,
  UpdateTaskTypeRequest,
} from '@core/types/task';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class TaskTaskTypeStore {
  // List state
  items: TaskTypeDefinition[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: TaskTypeDefinition | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, TaskTypeDefinition>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): TaskTypeDefinition | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: TaskTypeDefinition | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Delete task type
   */
  async DeleteTaskType(type_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await taskApi.DeleteTaskType(type_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== type_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === type_id) {
          this.currentItem = null;
        }

        this.removeFromCache(type_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get task type
   */
  async GetTaskType(type_id: string): Promise<TaskTypeDefinition> {
    // Check cache first
    const cached = this.cache.get(type_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await taskApi.GetTaskType(type_id);
      const item = response.definition;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List task types
   */
  async ListTaskTypes(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await taskApi.ListTaskTypes(params);

      runInAction(() => {
        this.items = response.definitions?.definitions ?? [];
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Register task type
   */
  async RegisterTaskType(data: RegisterTaskTypeRequest): Promise<TaskTypeDefinitionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await taskApi.RegisterTaskType(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update task type
   */
  async UpdateTaskType(type_id: string, data: UpdateTaskTypeRequest): Promise<TaskTypeDefinition> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await taskApi.UpdateTaskType(type_id, data);
      const item = response.definition;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const taskTaskTypeStore = new TaskTaskTypeStore();
